import React, { useState, useContext, useEffect, useRef } from "react";
import axios from "axios";
import { AppContext } from "../../../context/Appcontext";
import "../../../assests/css/teachers/TeacherAttendance.css";
import { FaQrcode, FaCheckCircle, FaTimes } from "react-icons/fa";
import { useNavigate } from "react-router-dom";
import { Html5Qrcode } from "html5-qrcode";
import TeacherMasterLayout from "../../../layouts/TeacherMasterLayout";
import Swal from "sweetalert2";

// Backend ki date/time string ko readable format mein dikhane ke liye
const formatMarkedAt = (value) => {
  if (!value) return "";
  const d = new Date(String(value).replace(" ", "T"));
  if (isNaN(d.getTime())) return String(value);
  return d.toLocaleString("en-GB", {
    day: "2-digit",
    month: "short",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
};

// NEW: current week (Sunday 00:00 se Saturday 23:59) ki range nikalta hai
const getCurrentWeekRange = () => {
  const now = new Date();
  const day = now.getDay(); // 0 = Sunday ... 6 = Saturday

  const start = new Date(now);
  start.setHours(0, 0, 0, 0);
  start.setDate(now.getDate() - day); // is week ka Sunday

  const end = new Date(start);
  end.setDate(start.getDate() + 6); // is week ka Saturday
  end.setHours(23, 59, 59, 999);

  return { start, end };
};

const TeacherAttendance = () => {
  const savedTeacher = JSON.parse(localStorage.getItem("teacher"));
  const teacherId = savedTeacher?.id || 1;

  const { baseUrl } = useContext(AppContext);
  const navigate = useNavigate();

  const [scanOpen, setScanOpen] = useState(false);
  const [scanning, setScanning] = useState(false);

  // aaj ki attendance ka status (server se aata hai)
  const [todayMarked, setTodayMarked] = useState(false);
  const [markedAt, setMarkedAt] = useState("");
  const [statusLoading, setStatusLoading] = useState(true);

  // NEW: is hafte (Sunday-Saturday) ki attendance history
  const [weekHistory, setWeekHistory] = useState([]);
  const [historyLoading, setHistoryLoading] = useState(true);

  // scan callback baar baar fire hota hai, isliye state ki jagah ref se lock karte hain
  const processingRef = useRef(false);
  const scannerRef = useRef(null);

  const detectDeviceInfo = () => {
    const ua = navigator.userAgent;
    let detectedOs = "Unknown OS";
    let detectedModel = ua;

    if (/Windows NT 10/.test(ua)) detectedOs = "Windows 10/11";
    else if (/Windows NT/.test(ua)) detectedOs = "Windows";
    else if (/Mac OS X (\d+[_\.]\d+)/.test(ua))
      detectedOs =
        "macOS " + ua.match(/Mac OS X (\d+[_\.]\d+)/)[1].replace("_", ".");
    else if (/Android (\d+(\.\d+)?)/.test(ua))
      detectedOs = "Android " + ua.match(/Android (\d+(\.\d+)?)/)[1];
    else if (/iPhone OS (\d+[_\.]\d+)/.test(ua))
      detectedOs = "iOS " + ua.match(/iPhone OS (\d+[_\.]\d+)/)[1].replace("_", ".");
    else if (/Linux/.test(ua)) detectedOs = "Linux";

    const modelMatch = ua.match(/\(([^)]+)\)/);
    if (modelMatch) detectedModel = modelMatch[1];

    return { os_version: detectedOs, device_model: detectedModel };
  };

  // page khulte hi server se poochho ke aaj attendance lag chuki hai ya nahi
  useEffect(() => {
    let cancelled = false;

    axios
      .get(`${baseUrl}/teacher/attendanceStatus/${teacherId}`)
      .then((res) => {
        if (cancelled) return;
        setTodayMarked(!!res.data?.marked);
        setMarkedAt(res.data?.marked_at || "");
      })
      .catch((err) => {
        console.error("Attendance status error:", err);
      })
      .finally(() => {
        if (!cancelled) setStatusLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [baseUrl, teacherId]);

  // NEW: page khulte hi is hafte ki attendance history fetch karo aur
  // Sunday-Saturday range mein filter kar do. Agla hafta shuru hote hi
  // getCurrentWeekRange() khud naya range dega, purana data list se hat jayega.
  useEffect(() => {
    let cancelled = false;

    axios
      .get(`${baseUrl}/teacher/attendanceHistory/${teacherId}`)
      .then((res) => {
        if (cancelled) return;

        const { start, end } = getCurrentWeekRange();

        const filtered = (res.data?.data || []).filter((rec) => {
          const recDate = new Date(String(rec.created_at).replace(" ", "T"));
          return recDate >= start && recDate <= end;
        });

        setWeekHistory(filtered);
      })
      .catch((err) => {
        console.error("Attendance history error:", err);
      })
      .finally(() => {
        if (!cancelled) setHistoryLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [baseUrl, teacherId]);

  const markAttendance = async (qrData) => {
    if (!qrData || processingRef.current) return;

    processingRef.current = true;
    setScanning(true);

    try {
      const response = await axios.post(`${baseUrl}/teacher/markAttendance`, {
        teacher_id: teacherId,
        qr_code: qrData,
        ...detectDeviceInfo(),
      });

      const nowMarkedAt = response.data?.marked_at || new Date().toISOString();

      // success par page ki state update, scanner band
      setTodayMarked(true);
      setMarkedAt(nowMarkedAt);

      // NEW: naya record turant is hafte ki history mein bhi daal do
      // (dobara fetch kiye bina UI turant update ho jaye)
      setWeekHistory((prev) => [
        { id: response.data?.data?.id ?? Date.now(), created_at: nowMarkedAt },
        ...prev,
      ]);

      setScanOpen(false);

      await Swal.fire({
        icon: "success",
        title: "Attendance Marked! 🎉",
        text: "Your attendance has been marked successfully.",
        confirmButtonText: "Okay 💜",
        confirmButtonColor: "#6A88CE",
        background: "#FEFEFF",
        borderRadius: "20px",
      });
    } catch (error) {
      // backend ne kaha "aaj pehle hi mark ho chuki hai" (409)
      if (error.response?.status === 409) {
        setTodayMarked(true);
        setMarkedAt(error.response.data?.marked_at || "");
        setScanOpen(false);
      }

      const message =
        error.response?.data?.message ||
        "Something went wrong. Please try again.";

      await Swal.fire({
        icon: "error",
        title: "Oops! 😔",
        text: message,
        confirmButtonText: "Okay",
        confirmButtonColor: "#6A88CE",
        background: "#FEFEFF",
        borderRadius: "20px",
      });
    } finally {
      setScanning(false);
      processingRef.current = false;
    }
  };

  const handleAttendanceDispute = () => {
    navigate("/teacher/helpsupport", {
      state: {
        request_type: "attendance_dispute",
        issue_type: "Attendance Issue",
        reference_id: teacherId,
        admin_id: 1,
      },
    });
  };

  // scanner kholne se pehle check
  const openScanner = () => {
    if (todayMarked) {
      Swal.fire({
        icon: "info",
        title: "Already marked",
        text: "Aaj ki attendance pehle hi mark ho chuki hai. Agli attendance kal lagegi.",
        confirmButtonText: "Okay",
        confirmButtonColor: "#6A88CE",
      });
      return;
    }
    setScanOpen(true);
  };

  // Scanner start/stop. Cleanup mein camera properly band hota hai.
  useEffect(() => {
    if (!scanOpen) return;

    let cancelled = false;
    let started = false;
    const html5QrCode = new Html5Qrcode("reader");
    scannerRef.current = html5QrCode;

    const stopScanner = () => {
      if (!started) return;
      started = false;
      html5QrCode
        .stop()
        .then(() => html5QrCode.clear())
        .catch(() => {});
    };

    Html5Qrcode.getCameras()
      .then((devices) => {
        if (cancelled) return;
        if (!devices || !devices.length) {
          console.error("No camera found");
          return;
        }

        const backCamera = devices.find((d) =>
          d.label.toLowerCase().includes("back")
        );
        const cameraId = backCamera
          ? backCamera.id
          : devices[devices.length - 1].id;

        return html5QrCode
          .start(
            cameraId,
            { fps: 20, qrbox: { width: 250, height: 250 } },
            (decodedText) => markAttendance(decodedText),
            () => {} // har frame par aane wala "QR not found" error ignore
          )
          .then(() => {
            started = true;
            // agar start hote hote modal band ho gaya to foran stop
            if (cancelled) stopScanner();
          });
      })
      .catch((err) => {
        console.error("Error while starting camera", err);
      });

    return () => {
      cancelled = true;
      stopScanner();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [scanOpen]);

  const closeScanner = () => {
    setScanOpen(false);
  };

  return (
    <TeacherMasterLayout>
      {/* Topbar */}
      <header className="ta-topbar">
        <h3>Welcome, Teacher 👋</h3>
        <div className="ta-profile">👤</div>
      </header>

      <div className="ta-page">
        {/* Header */}
        <div className="ta-header">
          <h1>Teacher Attendance</h1>
          <button
            className="ta-qr-btn"
            onClick={openScanner}
            disabled={statusLoading || todayMarked}
          >
            {todayMarked ? (
              <>
                <FaCheckCircle /> Attendance Marked
              </>
            ) : (
              <>
                <FaQrcode /> Scan QR
              </>
            )}
          </button>
        </div>

        {/* aaj ki attendance ka status */}
        {!statusLoading && (
          <div
            className={`ta-today-status ${
              todayMarked ? "ta-today-marked" : "ta-today-pending"
            }`}
          >
            {todayMarked ? (
              <>
                <FaCheckCircle size={22} />
                <div>
                  <strong>Aaj ki attendance mark ho chuki hai</strong>
                  {markedAt && <span>Marked at {formatMarkedAt(markedAt)}</span>}
                </div>
              </>
            ) : (
              <>
                <FaQrcode size={22} />
                <div>
                  <strong>Aaj ki attendance abhi baaki hai</strong>
                  <span>QR scan karke attendance mark karein.</span>
                </div>
              </>
            )}
          </div>
        )}

        {/* QR Info */}
        <div className="ta-qr-info">
          <FaCheckCircle size={42} />
          <div>
            <h2>QR Based Teacher Attendance</h2>
          </div>
        </div>

        {/* History: sirf is hafte (Sunday-Saturday) ki attendance */}
        <div className="ta-history">
          <h2>This Week's Attendance</h2>

          <div className="ta-mobile-list">
            {historyLoading ? (
              <p>Loading...</p>
            ) : weekHistory.length === 0 ? (
              <p>Is week abhi tak koi attendance mark nahi hui.</p>
            ) : (
              weekHistory.map((rec) => (
                <div key={rec.id} className="ta-mobile-card">
                  <div className="ta-row">
                    <span>Date</span>
                    <strong>{formatMarkedAt(rec.created_at)}</strong>
                  </div>
                  <div className="ta-row">
                    <span>Status</span>
                    <strong className="ta-status-present">Present</strong>
                  </div>
                </div>
              ))
            )}
          </div>
        </div>
      </div>

      {/* Scan Modal */}
      {scanOpen && (
        <div className="ta-scan-overlay">
          <div className="ta-scan-modal">
            <button className="ta-close-btn" onClick={closeScanner}>
              <FaTimes />
            </button>
            <button className="btn btn-danger" onClick={handleAttendanceDispute}>
              Raise Dispute
            </button>

            <h2>Scan Attendance QR</h2>

            <div className="ta-camera-box">
              <div id="reader" />
              {scanning && <p>Marking attendance...</p>}
            </div>
          </div>
        </div>
      )}
    </TeacherMasterLayout>
  );
};

export default TeacherAttendance;